import { gql } from "@apollo/client";

import * as fragments from "./fragments";

export const CREATE_ACCOUNT = gql`
	mutation CreateAccount(
		$email: String!
		$firstName: String!
		$lastName: String!
		$password: String!
		$termsAgreed: Boolean!
	) {
		createAccount(
			account: {
				email: $email
				firstName: $firstName
				lastName: $lastName
				password: $password
				termsAgreed: $termsAgreed
			}
		) {
			account {
				...accountFields
			}
			features {
				...featureFlagFields
			}
			session {
				...sessionFields
			}
		}
	}
	${fragments.ACCOUNT}
	${fragments.FEATURE_FLAG}
	${fragments.SESSION}
`;

export const ACTIVATE_ACCOUNT = gql`
	mutation AccountAccount(
		$token: String!
		$password: String!
		$termsAgreed: Boolean!
	) {
		activateAccount(
			form: { token: $token, password: $password, termsAgreed: $termsAgreed }
		) {
			account {
				...accountFields
			}
			features {
				...featureFlagFields
			}
			session {
				...sessionFields
			}
		}
	}
	${fragments.ACCOUNT}
	${fragments.FEATURE_FLAG}
	${fragments.SESSION}
`;

export const CONFIRM_ACCREDITATION = gql`
	mutation ConfirmAccreditation($id: ID!, $country: ID!, $file: Upload!) {
		confirmAccreditation(form: { id: $id, country: $country, file: $file }) {
			...investingEntityFields
		}
	}
	${fragments.INVESTING_ENTITY}
`;

export const PROVIDE_BENEFICIAL_OWNER_BASIC_INFORMATION = gql`
	mutation ProvideBeneficialOwnerBasicInformation(
		$token: String!
		$firstName: String!
		$middleName: String
		$lastName: String!
		$dateOfBirth: DayMonthYearInput!
		$autocompleteAddress: AutocompleteAddressInput
		$manualAddress: ManualAddressInput
		$consent: Boolean!
	) {
		provideBeneficialOwnerBasicInformation(
			form: {
				token: $token
				firstName: $firstName
				middleName: $middleName
				lastName: $lastName
				dateOfBirth: $dateOfBirth
				autocompleteAddress: $autocompleteAddress
				manualAddress: $manualAddress
				consent: $consent
			}
		) {
			...beneficialOwnerFields
		}
	}
	${fragments.BENEFICIAL_OWNER}
`;

export const CONFIRM_BENEFICIAL_OWNER_IDENTITY_DOCUMENT_DETAILS = gql`
	mutation ConfirmBeneficialOwnerIdentityDocumentDetails(
		$token: String!
		$type: IdentityDocumentType!
		$country: ID!
		$firstName: String!
		$middleName: String!
		$lastName: String!
		$dateOfBirth: DayMonthYearInput!
		$identityDocumentFields: [IdentityDocumentFieldInput!]!
	) {
		confirmBeneficialOwnerIdentityDocumentDetails(
			form: {
				token: $token
				type: $type
				country: $country
				firstName: $firstName
				middleName: $middleName
				lastName: $lastName
				dateOfBirth: $dateOfBirth
				identityDocumentFields: $identityDocumentFields
			}
		) {
			...beneficialOwnerFields
		}
	}
	${fragments.BENEFICIAL_OWNER}
`;

export const UPLOAD_BENEFICIAL_OWNER_ADDRESS_DOCUMENT = gql`
	mutation UploadBeneficialOwnerAddressDocument(
		$token: String!
		$file: Upload!
		$manualAddress: ManualAddressInput
		$autocompleteAddress: AutocompleteAddressInput
	) {
		uploadBeneficialOwnerAddressDocument(
			form: {
				token: $token
				file: $file
				manualAddress: $manualAddress
				autocompleteAddress: $autocompleteAddress
			}
		) {
			...beneficialOwnerFields
		}
	}
	${fragments.BENEFICIAL_OWNER}
`;

export const UPDATE_BANK_ACCOUNT = gql`
	mutation UpdateBankAccount($id: ID!, $nickname: String) {
		updateBankAccount(form: { id: $id, nickname: $nickname }) {
			...verifiableBankAccountFields
		}
	}
	${fragments.VERIFIABLE_BANK_ACCOUNT}
`;

export const UPLOAD_BANK_ACCOUNT_VERIFICATION_DOCUMENT = gql`
	mutation UploadBankAccountVerificationDocument(
		$id: ID!
		$name: String
		$nickname: String
		$accountNumber: String
		$bsbCode: String
		$routingNumber: String
		$sortCode: String
		$file: Upload!
	) {
		uploadBankAccountVerificationDocument(
			form: {
				id: $id
				name: $name
				nickname: $nickname
				accountNumber: $accountNumber
				bsbCode: $bsbCode
				routingNumber: $routingNumber
				sortCode: $sortCode
				file: $file
			}
		) {
			...verifiableBankAccountFields
		}
	}
	${fragments.VERIFIABLE_BANK_ACCOUNT}
`;

export const CREATE_NZD_BANK_ACCOUNT = gql`
	mutation CreateNZDBankAccount(
		$name: String
		$nickname: String!
		$accountNumber: String!
		$file: Upload!
		$investingEntityId: ID!
	) {
		createNZDBankAccount(
			form: {
				name: $name
				nickname: $nickname
				accountNumber: $accountNumber
				file: $file
				investingEntityId: $investingEntityId
			}
		) {
			...verifiableBankAccountFields
		}
	}
	${fragments.VERIFIABLE_BANK_ACCOUNT}
`;

export const CREATE_AUD_BANK_ACCOUNT = gql`
	mutation CreateAUDBankAccount(
		$name: String
		$nickname: String!
		$accountNumber: String!
		$bsbCode: String!
		$file: Upload!
		$investingEntityId: ID!
	) {
		createAUDBankAccount(
			form: {
				name: $name
				nickname: $nickname
				accountNumber: $accountNumber
				bsbCode: $bsbCode
				file: $file
				investingEntityId: $investingEntityId
			}
		) {
			...verifiableBankAccountFields
		}
	}
	${fragments.VERIFIABLE_BANK_ACCOUNT}
`;

export const CREATE_USD_BANK_ACCOUNT = gql`
	mutation CreateUSDBankAccount(
		$name: String
		$nickname: String!
		$accountNumber: String!
		$routingNumber: String!
		$file: Upload!
		$investingEntityId: ID!
	) {
		createUSDBankAccount(
			form: {
				name: $name
				nickname: $nickname
				accountNumber: $accountNumber
				routingNumber: $routingNumber
				file: $file
				investingEntityId: $investingEntityId
			}
		) {
			...verifiableBankAccountFields
		}
	}
	${fragments.VERIFIABLE_BANK_ACCOUNT}
`;

export const CREATE_GBP_BANK_ACCOUNT = gql`
	mutation CreateGBPBankAccount(
		$name: String
		$nickname: String!
		$accountNumber: String!
		$sortCode: String!
		$file: Upload!
		$investingEntityId: ID!
	) {
		createGBPBankAccount(
			form: {
				name: $name
				nickname: $nickname
				accountNumber: $accountNumber
				sortCode: $sortCode
				file: $file
				investingEntityId: $investingEntityId
			}
		) {
			...verifiableBankAccountFields
		}
	}
	${fragments.VERIFIABLE_BANK_ACCOUNT}
`;

export const CREATE_EUR_BANK_ACCOUNT = gql`
	mutation CreateEURBankAccount(
		$name: String
		$nickname: String!
		$accountNumber: String!
		$file: Upload!
		$investingEntityId: ID!
	) {
		createEURBankAccount(
			form: {
				name: $name
				nickname: $nickname
				accountNumber: $accountNumber
				file: $file
				investingEntityId: $investingEntityId
			}
		) {
			...verifiableBankAccountFields
		}
	}
	${fragments.VERIFIABLE_BANK_ACCOUNT}
`;

export const CREATE_SESSION = gql`
	mutation CreateSession($email: String!, $password: String!) {
		createSession(account: { email: $email, password: $password }) {
			account {
				...accountFields
			}
			features {
				...featureFlagFields
			}
			session {
				...sessionFields
			}
		}
	}
	${fragments.ACCOUNT}
	${fragments.FEATURE_FLAG}
	${fragments.SESSION}
`;

export const CREATE_SESSION_FROM_TOKEN = gql`
	mutation CreateSessionFromToken($token: String!) {
		createSessionFromToken(account: { token: $token }) {
			account {
				...accountFields
			}
			features {
				...featureFlagFields
			}
			session {
				...sessionFields
			}
		}
	}
	${fragments.ACCOUNT}
	${fragments.FEATURE_FLAG}
	${fragments.SESSION}
`;

export const COMPLETE_ACCOUNT_DETAILS = gql`
	mutation CompleteAccountDetails(
		$addressPlaceId: String
		$addressRawSearch: String
		$addressLine1: String!
		$addressCity: String!
		$addressSuburb: String!
		$addressPostCode: String!
		$addressCountry: String!
		$phoneNumber: String!
		$callingCode: String!
		$primaryCitizenshipCountry: String!
		$availableFunds: AccountAvailableFunds!
		$consent: Boolean!
	) {
		completeAccountDetails(
			form: {
				addressPlaceId: $addressPlaceId
				addressRawSearch: $addressRawSearch
				addressLine1: $addressLine1
				addressCity: $addressCity
				addressSuburb: $addressSuburb
				addressPostCode: $addressPostCode
				addressCountry: $addressCountry
				phoneNumber: $phoneNumber
				callingCode: $callingCode
				primaryCitizenshipCountry: $primaryCitizenshipCountry
				availableFunds: $availableFunds
				consent: $consent
			}
		) {
			...accountFields
		}
	}
	${fragments.ACCOUNT}
`;

export const COMPLETE_BENEFICIAL_OWNER_CONTACT_DETAILS = gql`
	mutation CompleteBeneficialOwnerContactDetails(
		$contactDetails: [BeneficialOwnerContactDetailsInput!]!
	) {
		completeBeneficialOwnerContactDetails(
			form: { contactDetails: $contactDetails }
		) {
			...investingEntityFields
		}
	}
	${fragments.INVESTING_ENTITY}
`;

export const DESTROY_SESSION = gql`
	mutation DestroySession($token: JWT!) {
		destroySession(session: { token: $token }) {
			message
			statusCode
		}
	}
`;

export const RESEND_EMAIL_CONFIRMATION = gql`
	mutation ResendEmailConfirmation($email: String!) {
		resendEmailConfirmation(account: { email: $email }) {
			message
			statusCode
		}
	}
`;

export const RESET_PASSWORD = gql`
	mutation ResetPassword($email: String!) {
		resetPassword(account: { email: $email }) {
			message
			statusCode
		}
	}
`;

export const SEND_CHANGE_PASSWORD_EMAIL = gql`
	mutation SendChangePasswordEmail {
		sendChangePasswordEmail {
			id
			email
		}
	}
`;

export const SET_NEW_PASSWORD = gql`
	mutation SetNewPassword(
		$token: String!
		$newPassword: String!
		$confirmPassword: String!
	) {
		setNewPassword(
			form: {
				newPassword: $newPassword
				confirmPassword: $confirmPassword
				token: $token
			}
		) {
			account {
				...accountFields
			}
			features {
				...featureFlagFields
			}
			session {
				...sessionFields
			}
		}
	}
	${fragments.ACCOUNT}
	${fragments.FEATURE_FLAG}
	${fragments.SESSION}
`;

export const UPDATE_PROFILE = gql`
	mutation UpdateProfile(
		$timeZone: String
		$callingCode: String
		$phoneNumber: String
		$preferredName: String
		$autocompleteAddress: AutocompleteAddressInput
		$manualAddress: ManualAddressInput
	) {
		updateProfile(
			form: {
				timeZone: $timeZone
				callingCode: $callingCode
				phoneNumber: $phoneNumber
				preferredName: $preferredName
				autocompleteAddress: $autocompleteAddress
				manualAddress: $manualAddress
			}
		) {
			...accountFields
		}
	}
	${fragments.ACCOUNT}
`;

export const UPLOAD_ADDRESS_VERIFICATION_DOCUMENT = gql`
	mutation UploadAddressVerificationDocument(
		$addressCity: String
		$addressCountry: String
		$addressLine1: String
		$addressPlaceId: String
		$addressPostCode: String
		$addressRawSearch: String
		$addressSuburb: String
		$file: Upload!
	) {
		uploadAddressVerificationDocument(
			form: {
				addressCity: $addressCity
				addressCountry: $addressCountry
				addressLine1: $addressLine1
				addressRawSearch: $addressRawSearch
				addressSuburb: $addressSuburb
				addressPlaceId: $addressPlaceId
				addressPostCode: $addressPostCode
				file: $file
			}
		) {
			...accountFields
		}
	}
	${fragments.ACCOUNT}
`;

export const UPLOAD_INVESTING_ENTITY_DOCUMENTS = gql`
	mutation UploadInvestingEntityDocuments(
		$investingEntityId: ID!
		$files: [Upload!]!
	) {
		uploadInvestingEntityDocuments(
			form: { investingEntityId: $investingEntityId, files: $files }
		) {
			investingEntity {
				id
				documents {
					...remoteAssetFields
				}
			}
		}
	}
	${fragments.REMOTE_ASSET}
`;

export const PROVIDE_SOURCE_OF_FUNDS = gql`
	mutation ProvideSourceOfFunds(
		$id: ID!
		$sourceOfFunds: SourceOfFundsSource!
		$document: Upload!
		$supportingInformation: String
	) {
		provideSourceOfFunds(
			form: {
				id: $id
				sourceOfFunds: $sourceOfFunds
				document: $document
				supportingInformation: $supportingInformation
			}
		) {
			...investingEntityFields
		}
	}
	${fragments.INVESTING_ENTITY}
`;
